import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { BulkAssignGame, BulkAssignSaveArgs, BulkAssignSaveResult, GameChoiceMap } from '../features/categories/types/BulkAssign'
import { InternalCategoryFeature } from '../types/InternalCategory'
import { legacyAccept, optionalJsonOrText } from './helpers'
import { categoryFeaturesTag, internalApiserverApi } from './internal-apiserver'

// The backend ignores `include` and answers whole games; legacy sends it, so the request stays the same.
const bulkGamesInclude = ['icon', 'icons', 'name', 'appId', 'artist', 'gpsPerMarket', 'conventionalSubgenre'].join(',')

// Market ISO codes are stored in mixed case (`gb`, `DK`) and must be sent as stored.
const isoList = (isos: string[]) => encodeURIComponent(isos.join(','))
const gameChoicesUrl = (legacyId: number, isos: string[]) => `i/features/${legacyId}/games/${isoList(isos)}`

const gameChoicesTag = (legacyId: number) => ({ type: 'internalFeatureGameChoices' as const, id: legacyId })

const toBulkAssignGame = (game: BulkAssignGame): BulkAssignGame => ({
  id: game.id,
  name: game.name,
  appId: game.appId,
  artist: game.artist,
  icon: game.icon,
  icons: game.icons,
  gpsPerMarket: game.gpsPerMarket,
  conventionalSubgenre: game.conventionalSubgenre,
})

const featureBulkAssignApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalFeatureGameChoices'] }).injectEndpoints({
  endpoints: (builder) => ({
    getInternalFeatureGameChoices: builder.query<GameChoiceMap, { legacyId: number; isos: string[] }>({
      query: ({ legacyId, isos }) => gameChoicesUrl(legacyId, isos),
      providesTags: (_result, _error, { legacyId }) => [gameChoicesTag(legacyId)],
    }),
    getInternalBulkAssignGames: builder.query<BulkAssignGame[], { isos: string[] }>({
      query: ({ isos }) => ({ url: 'i/games/list/bulk', params: { marketIso: isos.join(','), include: bulkGamesInclude } }),
      transformResponse: (games: BulkAssignGame[]) => games.map(toBulkAssignGame),
    }),
    /** The category's active features in the market (legacy's benchmark picker). */
    getInternalCategoryMarketFeatures: builder.query<InternalCategoryFeature[], { categoryId: string; marketIso: string }>({
      query: ({ categoryId, marketIso }) => `i/categories/${categoryId}/features/${encodeURIComponent(marketIso)}`,
      providesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId)],
    }),
    // Writes every listed market of every game in the body; read back market by market.
    saveInternalFeatureBulkAssign: builder.mutation<BulkAssignSaveResult, BulkAssignSaveArgs>({
      async queryFn({ legacyId, isos, answers }, _api, _extraOptions, baseQuery) {
        // The bulk update answers with an empty body.
        const saved = await baseQuery({
          url: `i/features/bulkupdate/${isoList(isos)}`,
          method: 'POST',
          body: answers,
          headers: legacyAccept,
          responseHandler: optionalJsonOrText,
        })
        if (saved.error) return { error: saved.error }
        const reads = await Promise.all(isos.map((iso) => baseQuery(gameChoicesUrl(legacyId, [iso]))))
        const failed = reads.find((read) => read.error)
        if (failed) return { error: failed.error as FetchBaseQueryError }
        return { data: { storedByIso: Object.fromEntries(isos.map((iso, index) => [iso, (reads[index].data as GameChoiceMap | null) ?? {}])) } }
      },
      invalidatesTags: (_result, _error, { legacyId }) => [gameChoicesTag(legacyId)],
    }),
  }),
})

export const {
  useGetInternalFeatureGameChoicesQuery,
  useGetInternalBulkAssignGamesQuery,
  useGetInternalCategoryMarketFeaturesQuery,
  useSaveInternalFeatureBulkAssignMutation,
} = featureBulkAssignApi
